import {
  convertColorToHSL,
  generateThemeColor,
} from "senf-atomic-design-system/src/styles/helpers";
import { useTranslation } from "react-i18next";
import GeoJSONUploader from "./GeoJSONUploader";
import GisDataset from "./GisDataset";
import BimUploader, { SavedAsset } from "./BimUploader";
import SavedModels from "./SavedModels";
import { useAuthContext } from "senf-shared/index";
import { useModals } from "../../../../molecules/modalStack/ModalProvider";
import Button from "../../../../core/button/Button";

function fixEncoding(incorrectText) {
  const bytes = [];
  for (let i = 0; i < incorrectText.length; i++) {
    bytes.push(incorrectText.charCodeAt(i));
  }
  const utf8Decoder = new TextDecoder("utf-8");
  const byteArray = new Uint8Array(bytes);
  const correctText = utf8Decoder.decode(byteArray);
  return correctText;
}

function isEncodingIncorrect(text) {
  // You can customize this function to better identify incorrectly encoded strings
  return text.includes("Ã");
}

const GisUploadBuilder = ({ formik, setDrawMode, initialColor }) => {
  const { user } = useAuthContext();
  const { t } = useTranslation();
  const { openModal, closeModal } = useModals();

  // Handle BIM asset saved to Formik
  const handleBimAssetSaved = (asset: SavedAsset) => {
    console.log('🎉 BIM Asset saved to Formik:', asset);
    
    // Initialize bimModels array if it doesn't exist
    if (!formik.values.bimModels) {
      formik.setFieldValue('bimModels', []);
    }
    
    // Add the new asset to Formik values
    const currentModels = formik.values.bimModels || [];
    formik.setFieldValue('bimModels', [...currentModels, asset]);
  };

  // Handle removing BIM asset
  const handleRemoveBimAsset = (assetId: number) => {
    const currentModels = formik.values.bimModels || [];
    const updatedModels = currentModels.filter(
      (model: SavedAsset) => model.assetId !== assetId
    );
    formik.setFieldValue('bimModels', updatedModels);
  };

  // Open BIM upload modal
  const openBimUploadModal = () => {
    openModal(
      <BimUploader 
        onAssetSaved={handleBimAssetSaved}
        onClose={() => closeModal({ id: "bim-upload-modal" })}
      />,
      {
        title: "BIM Upload",
        size: "xl",
        id: "bim-upload-modal",
        zIndex: 9999999,
      }
    );
  };
  const handleUpload = (data, type, indexAddition = 0) => {
    setDrawMode({ mode: "draw", drawType: null });

    if (type !== "geojson") {
      const dataIndex = formik.values.gisData.length; // Get the current length of the array
      formik.setFieldValue(`gisData[${dataIndex}].orginialType`, type);
    }

    if (["geojson", "csv", "json", "zip"].includes(type)) {
      const dataIndex = formik.values.gisData.length + indexAddition; // Get the current length of the array

      formik.setFieldValue(`gisData[${dataIndex}].type`, "geojson");
      formik.setFieldValue(`gisData[${dataIndex}].layerTitle`, data.title);
      formik.setFieldValue(`gisData[${dataIndex}].selectedAttribute`, "none");

      const prevFeaturesLength =
        formik.values.gisData?.[dataIndex]?.data?.features?.length || 0;

      const updatedFeatures = data?.data?.features.map((feature, index) => {
        const updatedProperties = { ...feature.properties };

        // Iterate through each property and fix encoding if necessary
        for (const key in updatedProperties) {
          if (
            typeof updatedProperties[key] === "string" &&
            isEncodingIncorrect(updatedProperties[key])
          ) {
            updatedProperties[key] = fixEncoding(updatedProperties[key]);
          }
        }

        return {
          ...feature,
          properties: {
            ...updatedProperties,
            color: initialColor,
            labelColor: generateThemeColor(convertColorToHSL(initialColor))
              ?.color["800"],
            number: prevFeaturesLength + index + 1,
            datasetIndex: dataIndex,
            tooltipLabel: "none",
          },
        };
      });

      const modifiedData = {
        type: "FeatureCollection",
        features: [
          ...(formik.values.gisData?.[dataIndex]?.data?.features || []),
          ...updatedFeatures,
        ],
      };

      setTimeout(() => {
        formik.setFieldValue(`gisData[${dataIndex}].data`, modifiedData);
        formik.setFieldValue(`gisData[${dataIndex}].title`, data.title);
      }, 100);
    }
  };

  return (
    <div className="flex flex-col gap-5 pb-5">
      <p className="body-md">{t("map:builder.gisUpload.infoText")}</p>
      <GeoJSONUploader
        text={`${t("util:upload", {
          val: `${t("global:geodata")}`,
        })} (WGS84 / UTM zone 32 / EPSG::25832)`}
        onFileUpload={handleUpload}
        // fileTypes={["geojson", "json", "csv", "zip"]}
        fileTypes={["geojson", "json", "csv", "zip"]}
        fileTypesText="GeoJSON, JSON, CSV, Shapefiles-zip"
      />
      {formik.values.gisData?.map((data, index) => {
        if (index === 0) return;

        return (
          <GisDataset
            formik={formik}
            index={index}
            initialColor={initialColor}
          />
        );
      })}


      {user?.isSuperAdmin && import.meta.env.VITE_APP_MODE === "development" && (

        <Button
          text="Upload BIM"
          size="lg"
          leadingIcon="plus-md"
          onClick={openBimUploadModal}
        />
      )}

      {/* Display BIM Models */}
      {user?.isSuperAdmin && import.meta.env.VITE_APP_MODE === "development" && formik.values.bimModels && formik.values.bimModels.length > 0 && (
        <SavedModels 
          assets={formik.values.bimModels}
          onRemove={handleRemoveBimAsset}
        />
      )}
    </div>
  );
};

export default GisUploadBuilder;
